/**
 * roto.js — manual rotoscoping core for video-bg-remover.
 *
 * Layers hand-painted corrections ON TOP of the AI (RVM) alpha matte. It is
 * the pure data + compositing core — no editor DOM lives here (that is
 * roto-editor.js). The pipeline and the editor both reach the final alpha
 * through one method, applyTo(), so preview always equals export.
 *
 * Data model — sparse + vector:
 *   • Most frames are untouched (the AI matte is fine) → nothing is stored.
 *   • A painted frame stores an ordered list of vector strokes (the source of
 *     truth — resolution-independent, cheap, trivially undoable).
 *   • A rasterised 1-channel correction canvas is cached in a bounded LRU and
 *     re-derived from the stroke list on a miss. No per-frame bitmap is ever
 *     held — worst-case memory is the LRU, not the frame count.
 *   • Propagation "ranges" let one painted frame (a keyframe) hold across a
 *     contiguous span of frames.
 *
 * Correction pixel value (stored in the R channel of the correction canvas):
 *   128  = neutral, no correction (every untouched pixel)
 *   >128 = push toward opaque  (KEEP brush — restore foreground)
 *   <128 = push toward transparent (REMOVE brush — erase background)
 *
 * Final alpha = applyTo(aiAlpha, c):
 *   c == 128 → aiAlpha
 *   c >  128 → lerp(aiAlpha, 255, (c-128)/127)
 *   c <  128 → aiAlpha * (c/128)
 *
 * Public API — window.VbrRoto:
 *   configure(w, h)                     set the working (correction) resolution
 *   addStroke(frame, stroke)            commit a finished stroke (undoable)
 *   clearFrame(frame)                   drop all corrections on a frame (undoable)
 *   setHold(keyframe, endFrame)         hold a keyframe's correction to endFrame
 *   hasAnyEdits()                       any manual edit anywhere?
 *   isFrameAffected(frame)              does this frame carry a correction?
 *   keyframes()                         sorted array of painted frame indices
 *   ranges()                            propagation ranges
 *   applyTo(frame, matteCanvas)         → corrected RGBA canvas (or same ref)
 *   rasterize(frame)                    → correction canvas for a frame (or null)
 *   stampStroke(ctx, stroke)            paint a stroke onto an arbitrary ctx
 *   undo() / redo() / canUndo() / canRedo()
 *   reset()
 *   toJSON() / loadJSON(obj)
 *
 * A "stroke" object: { mode:'keep'|'remove'|'erase', size, softness, pts:[x,y,…] }
 * Coordinates are in working-resolution pixels.
 */
(function (root) {
    'use strict';

    var MAX_WORK_EDGE = 1920;   // correction layer is capped — a hand brush
                                // carries no 4K detail; upscaled at export.
    var LRU_LIMIT = 24;         // decoded correction canvases kept warm.

    var work = { w: 0, h: 0 };
    var corrections = new Map();      // frame → { strokes:[…] }
    var holds = [];                   // [{ start, end, source }]
    var rasterLRU = new Map();        // keyframe → canvas (insertion-ordered)
    var undoStack = [];
    var redoStack = [];
    var scratch = null;               // reused canvas for applyTo scaling

    /* ───────────────────────── geometry ───────────────────────── */

    function configure(w, h) {
        w = Math.max(2, Math.round(w || 0));
        h = Math.max(2, Math.round(h || 0));
        var long = Math.max(w, h);
        if (long > MAX_WORK_EDGE) {
            var k = MAX_WORK_EDGE / long;
            w = Math.round(w * k);
            h = Math.round(h * k);
        }
        if (w === work.w && h === work.h) return;
        work.w = w; work.h = h;
        rasterLRU.clear();            // resolution changed — caches are stale
    }

    function makeCanvas(w, h) {
        var c = (typeof OffscreenCanvas !== 'undefined')
            ? new OffscreenCanvas(w, h)
            : document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }

    /* ───────────────────────── brush stamping ───────────────────────── */

    function strokeColor(mode, alpha) {
        if (mode === 'keep')   return 'rgba(255,255,255,' + alpha + ')';
        if (mode === 'remove') return 'rgba(0,0,0,' + alpha + ')';
        return 'rgba(128,128,128,' + alpha + ')';   // erase → restore neutral
    }

    /**
     * Paint one stroke's polyline onto ctx with a soft radial brush. Used both
     * to rasterise stored strokes and (by the editor) to paint the live layer.
     */
    function stampStroke(ctx, stroke) {
        var pts = stroke.pts;
        if (!pts || pts.length < 2) return;
        var size = Math.max(1, stroke.size || 12);
        var soft = Math.min(1, Math.max(0, stroke.softness == null ? 0.5 : stroke.softness));
        var r = size / 2;
        var hard = Math.max(0.02, 1 - soft);   // inner solid fraction
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';

        function dab(x, y) {
            var g = ctx.createRadialGradient(x, y, 0, x, y, r);
            var col0 = strokeColor(stroke.mode, 1);
            var col1 = strokeColor(stroke.mode, 0);
            g.addColorStop(0, col0);
            g.addColorStop(hard, col0);
            g.addColorStop(1, col1);
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, 6.283185307);
            ctx.fill();
        }

        // Single point → one dab.
        if (pts.length === 2) { dab(pts[0], pts[1]); ctx.restore(); return; }

        var spacing = Math.max(1, size * 0.18);
        for (var i = 2; i < pts.length; i += 2) {
            var x0 = pts[i - 2], y0 = pts[i - 1], x1 = pts[i], y1 = pts[i + 1];
            var dx = x1 - x0, dy = y1 - y0;
            var dist = Math.sqrt(dx * dx + dy * dy);
            var steps = Math.max(1, Math.ceil(dist / spacing));
            for (var s = 0; s <= steps; s++) {
                var t = s / steps;
                dab(x0 + dx * t, y0 + dy * t);
            }
        }
        ctx.restore();
    }

    /* ───────────────────────── rasterisation + LRU ───────────────────────── */

    function lruGet(key) {
        if (!rasterLRU.has(key)) return null;
        var v = rasterLRU.get(key);
        rasterLRU.delete(key); rasterLRU.set(key, v);   // bump recency
        return v;
    }

    function lruSet(key, canvas) {
        rasterLRU.set(key, canvas);
        while (rasterLRU.size > LRU_LIMIT) {
            var oldest = rasterLRU.keys().next().value;
            rasterLRU.delete(oldest);
        }
    }

    function rasterizeKeyframe(kf) {
        var hit = lruGet(kf);
        if (hit) return hit;
        var entry = corrections.get(kf);
        var c = makeCanvas(work.w, work.h);
        var ctx = c.getContext('2d');
        ctx.fillStyle = 'rgb(128,128,128)';
        ctx.fillRect(0, 0, work.w, work.h);
        if (entry && entry.strokes) {
            for (var i = 0; i < entry.strokes.length; i++) stampStroke(ctx, entry.strokes[i]);
        }
        lruSet(kf, c);
        return c;
    }

    function invalidate(kf) {
        rasterLRU.delete(kf);
    }

    /**
     * Which keyframe's correction governs `frame`: the frame's own correction
     * if it has one, else a hold range whose source it falls inside.
     */
    function resolveKeyframe(frame) {
        if (corrections.has(frame)) return frame;
        for (var i = 0; i < holds.length; i++) {
            var h = holds[i];
            if (frame >= h.start && frame <= h.end && corrections.has(h.source)) return h.source;
        }
        return -1;
    }

    /* ───────────────────────── public: edits ───────────────────────── */

    function pushUndo(cmd) {
        undoStack.push(cmd);
        if (undoStack.length > 240) undoStack.shift();
        redoStack.length = 0;
    }

    function addStroke(frame, stroke) {
        frame = frame | 0;
        var entry = corrections.get(frame);
        if (!entry) { entry = { strokes: [] }; corrections.set(frame, entry); }
        entry.strokes.push(stroke);
        invalidate(frame);
        pushUndo({ type: 'addStroke', frame: frame });
        return frame;
    }

    function clearFrame(frame) {
        frame = frame | 0;
        var entry = corrections.get(frame);
        if (!entry) return;
        corrections.delete(frame);
        invalidate(frame);
        // Holds sourced from this frame become inert; keep them for redo.
        pushUndo({ type: 'clearFrame', frame: frame, strokes: entry.strokes });
    }

    function setHold(keyframe, endFrame) {
        keyframe = keyframe | 0; endFrame = endFrame | 0;
        if (endFrame <= keyframe || !corrections.has(keyframe)) return;
        var before = holds.slice();
        // Drop any existing hold from this keyframe, then add the new span.
        holds = holds.filter(function (h) { return h.source !== keyframe; });
        holds.push({ start: keyframe, end: endFrame, source: keyframe });
        pushUndo({ type: 'setHold', before: before, after: holds.slice() });
    }

    /* ───────────────────────── public: queries ───────────────────────── */

    function hasAnyEdits() { return corrections.size > 0; }

    function isFrameAffected(frame) { return resolveKeyframe(frame | 0) >= 0; }

    function keyframes() {
        return Array.from(corrections.keys()).sort(function (a, b) { return a - b; });
    }

    function ranges() { return holds.slice(); }

    /* ───────────────────────── public: compositing seam ───────────────────────── */

    function rasterize(frame) {
        var kf = resolveKeyframe(frame | 0);
        return kf < 0 ? null : rasterizeKeyframe(kf);
    }

    /**
     * Composite an explicit correction canvas onto a matte frame. This is the
     * single formula used by BOTH the export pipeline and the editor preview,
     * so what the user paints is exactly what the export bakes.
     * matteCanvas is never mutated; a new RGBA canvas is returned.
     */
    function applyCorrectionCanvas(matteCanvas, corrCanvas) {
        var w = matteCanvas.width, h = matteCanvas.height;
        if (!w || !h) return matteCanvas;
        if (!scratch) scratch = makeCanvas(w, h);
        if (scratch.width !== w || scratch.height !== h) { scratch.width = w; scratch.height = h; }
        var sctx = scratch.getContext('2d');
        sctx.clearRect(0, 0, w, h);
        sctx.drawImage(corrCanvas, 0, 0, w, h);    // scale correction to matte res
        var cd = sctx.getImageData(0, 0, w, h).data;

        var mctx = matteCanvas.getContext('2d');
        var md = mctx.getImageData(0, 0, w, h);    // a copy — matteCanvas untouched
        var d = md.data;
        for (var i = 0; i < d.length; i += 4) {
            var c = cd[i];                          // correction value (R channel)
            if (c === 128) continue;
            var a = d[i + 3];
            if (c > 128) d[i + 3] = Math.round(a + (255 - a) * ((c - 128) / 127));
            else         d[i + 3] = Math.round(a * (c / 128));
        }
        var out = makeCanvas(w, h);
        out.getContext('2d').putImageData(md, 0, 0);
        return out;
    }

    /**
     * The integration seam. Given a frame index and the RVM matte frame (an
     * RGBA canvas: RGB = decontaminated foreground, A = alpha matte), return a
     * corrected RGBA canvas. Unedited frames return the SAME reference (zero
     * copy). matteCanvas is never mutated.
     */
    function applyTo(frame, matteCanvas) {
        var kf = resolveKeyframe(frame | 0);
        if (kf < 0) return matteCanvas;
        if (!matteCanvas.width || !matteCanvas.height) return matteCanvas;
        return applyCorrectionCanvas(matteCanvas, rasterizeKeyframe(kf));
    }

    /** Build a neutral (128) correction canvas at the working resolution. */
    function blankCorrection() {
        var c = makeCanvas(work.w, work.h);
        var ctx = c.getContext('2d');
        ctx.fillStyle = 'rgb(128,128,128)';
        ctx.fillRect(0, 0, work.w, work.h);
        return c;
    }

    /* ───────────────────────── public: history ───────────────────────── */

    function applyInverse(cmd, redo) {
        if (cmd.type === 'addStroke') {
            var e = corrections.get(cmd.frame);
            if (redo) {
                // redo of addStroke is re-handled by storing the stroke; see undo()
            } else if (e) {
                cmd.popped = e.strokes.pop();
                if (!e.strokes.length) corrections.delete(cmd.frame);
                invalidate(cmd.frame);
            }
        }
    }

    function undo() {
        if (!undoStack.length) return null;
        var cmd = undoStack.pop();
        if (cmd.type === 'addStroke') {
            var e = corrections.get(cmd.frame);
            if (e) {
                cmd.popped = e.strokes.pop();
                if (!e.strokes.length) corrections.delete(cmd.frame);
                invalidate(cmd.frame);
            }
        } else if (cmd.type === 'clearFrame') {
            corrections.set(cmd.frame, { strokes: cmd.strokes.slice() });
            invalidate(cmd.frame);
        } else if (cmd.type === 'setHold') {
            holds = cmd.before.slice();
        }
        redoStack.push(cmd);
        return cmd.frame != null ? cmd.frame : (cmd.after && cmd.after[0] && cmd.after[0].source);
    }

    function redo() {
        if (!redoStack.length) return null;
        var cmd = redoStack.pop();
        if (cmd.type === 'addStroke') {
            var e = corrections.get(cmd.frame);
            if (!e) { e = { strokes: [] }; corrections.set(cmd.frame, e); }
            if (cmd.popped) e.strokes.push(cmd.popped);
            invalidate(cmd.frame);
        } else if (cmd.type === 'clearFrame') {
            corrections.delete(cmd.frame);
            invalidate(cmd.frame);
        } else if (cmd.type === 'setHold') {
            holds = cmd.after.slice();
        }
        undoStack.push(cmd);
        return cmd.frame != null ? cmd.frame : (cmd.after && cmd.after[0] && cmd.after[0].source);
    }

    function canUndo() { return undoStack.length > 0; }
    function canRedo() { return redoStack.length > 0; }

    /* ───────────────────────── public: lifecycle ───────────────────────── */

    function reset() {
        corrections.clear();
        holds.length = 0;
        rasterLRU.clear();
        undoStack.length = 0;
        redoStack.length = 0;
    }

    function toJSON() {
        var frames = {};
        corrections.forEach(function (v, k) {
            frames[k] = v.strokes.map(function (s) {
                return { mode: s.mode, size: s.size, softness: s.softness, pts: Array.from(s.pts) };
            });
        });
        return { w: work.w, h: work.h, frames: frames, holds: holds.slice() };
    }

    function loadJSON(obj) {
        reset();
        if (!obj) return;
        configure(obj.w || 2, obj.h || 2);
        if (obj.frames) {
            Object.keys(obj.frames).forEach(function (k) {
                corrections.set(k | 0, { strokes: obj.frames[k].slice() });
            });
        }
        if (obj.holds) holds = obj.holds.slice();
    }

    root.VbrRoto = {
        configure: configure,
        addStroke: addStroke,
        clearFrame: clearFrame,
        setHold: setHold,
        hasAnyEdits: hasAnyEdits,
        isFrameAffected: isFrameAffected,
        keyframes: keyframes,
        ranges: ranges,
        applyTo: applyTo,
        applyCorrectionCanvas: applyCorrectionCanvas,
        blankCorrection: blankCorrection,
        rasterize: rasterize,
        stampStroke: stampStroke,
        undo: undo, redo: redo, canUndo: canUndo, canRedo: canRedo,
        reset: reset,
        toJSON: toJSON, loadJSON: loadJSON,
        _work: work,
    };
})(typeof window !== 'undefined' ? window : globalThis);
